// src/components/fix-actions/redesign/artifact-detail-tray.tsx
//
// Redesigned artifact detail ("view") slide-over. Same prop contract as the
// legacy ArtifactDetailTray. Status changes go through the real onStatusChange
// (which fires the publish -> deploy-log chain); regenerate goes through the
// real onRegenerate; content is the real fullContent.

import { useState } from "react";
import type { StrategyArtifact } from "../../../services/fix-actions/optimization-studio-service";
import type { DeployStatus } from "../../../services/fix-actions/fix-actions-service";
import { FullContentRenderer } from "../full-content-renderer";
import { statusLabel, statusPillCss, typeColor, Ic } from "./shared";
import { ExplainPopover, type ExplainAnchor } from "./explain-popover";
import "./action-items-redesign.css";
import { ClaimCheckDetails } from "../artifact-claim-check";

const STATUS_OPTIONS: DeployStatus[] = ["Draft", "In Review", "Ready", "Published"];

export interface RedesignArtifactDetailTrayProps {
  artifact: StrategyArtifact;
  brandId: string;
  onClose: () => void;
  onRegenerate: (artifactId: string) => Promise<StrategyArtifact | null>;
  onStatusChange: (artifactId: string, status: DeployStatus) => void;
  isAssistantCollapsed?: boolean;
}

function extractText(fullContent: Record<string, any> | undefined, fallback: string): string {
  if (!fullContent) return fallback;
  const c: any = fullContent;
  for (const k of ["text", "body", "copy", "content", "markdown"]) {
    if (typeof c[k] === "string" && c[k].trim()) return c[k];
  }
  try { return JSON.stringify(fullContent, null, 2); } catch { return fallback; }
}

export function RedesignArtifactDetailTray({
  artifact,
  onClose,
  onRegenerate,
  onStatusChange,
}: RedesignArtifactDetailTrayProps) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [statusMenu, setStatusMenu] = useState(false);
  const [explain, setExplain] = useState<ExplainAnchor | null>(null);

  const label = statusLabel(artifact.status);
  const tc = typeColor(String(artifact.type));
  const text = extractText(artifact.fullContent, artifact.name);
  const isPublished = label === "Published";
  const dep = artifact.deploymentTargets || [];

  const ghostBtn = (content: React.ReactNode, onClick?: () => void, disabled?: boolean) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, padding: "8px 13px", borderRadius: 8, cursor: disabled ? "default" : "pointer", whiteSpace: "nowrap", border: "1px solid var(--border)", background: "var(--background)", color: "var(--foreground)", opacity: disabled ? 0.6 : 1 }}
    >
      {content}
    </button>
  );

  const explainType = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setExplain({ type: String(artifact.type), x: r.left, y: r.bottom + 8 });
  };

  return (
    <div className="ai-redesign">
      <div onClick={onClose} className="ai-fade-in" style={{ position: "fixed", inset: 0, background: "rgba(13,13,15,.32)", zIndex: 60 }} />
      <div className="ai-tray-in" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 680, maxWidth: "94vw", background: "var(--card)", borderLeft: "1px solid var(--border)", boxShadow: "var(--elevation-lg)", zIndex: 61, display: "flex", flexDirection: "column" }}>
        {/* header */}
        <div style={{ position: "relative", borderBottom: "1px solid var(--border)", padding: "22px 56px 20px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", margin: 0 }}>{artifact.name}</h3>
            <button onClick={explainType} style={{ fontSize: 12, fontWeight: 600, color: tc, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: "2px", textDecorationStyle: "dotted" }}>{String(artifact.type)}</button>
            <span style={statusPillCss(label)}>{label}</span>
          </div>
          <div style={{ fontSize: 13, color: "var(--muted-foreground)", marginTop: 5 }}>
            Supporting: <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>{artifact.supportingActionTitle || "-"}</strong>
            {artifact.strategicTheme ? `  ·  ${artifact.strategicTheme}` : ""}
          </div>
          {dep.length > 0 && (
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 11 }}>
              {dep.map((d, i) => (
                <span key={i} style={{ fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)", background: "var(--background)", border: "1px solid var(--border)", borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap" }}>{d}</span>
              ))}
            </div>
          )}

          {/* actions */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <button
              onClick={() => { try { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ } }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, padding: "8px 15px", borderRadius: 8, border: "none", background: "var(--alpha-teal)", color: "var(--accent-foreground)", cursor: "pointer" }}
            >
              <Ic paths={["M9 9h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z", "M5 15a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2"]} size={14} sw={1.8} />
              {copied ? "Copied" : "Copy to publish"}
            </button>
            {ghostBtn(
              <>{isPublished ? <Ic paths={["m5 12 5 5L20 7"]} size={14} sw={2} /> : null}{isPublished ? "Published" : "Send to Brand Publisher"}</>,
              isPublished ? undefined : () => onStatusChange(artifact.id, "Published"),
              isPublished
            )}
            {/* change status */}
            <div style={{ position: "relative" }}>
              {ghostBtn(<>Status <Ic paths={["m6 9 6 6 6-6"]} size={12} sw={2} /></>, () => setStatusMenu((v) => !v))}
              {statusMenu && (
                <>
                  <div onClick={() => setStatusMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 62 }} />
                  <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 63, minWidth: 170, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--elevation-md)", padding: 6 }}>
                    {STATUS_OPTIONS.filter((s) => s !== "Published").map((s) => {
                      const active = statusLabel(s) === label;
                      return (
                        <button
                          key={s}
                          onClick={() => { setStatusMenu(false); onStatusChange(artifact.id, s); }}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", fontSize: 13, fontWeight: active ? 600 : 500, color: active ? "var(--alpha-teal)" : "var(--foreground)", background: active ? "var(--teal-pill-bg, var(--alpha-teal-10))" : "transparent", border: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer" }}
                        >
                          {statusLabel(s)}
                          {active && <Ic paths={["m5 12 5 5L20 7"]} size={14} sw={2.4} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {ghostBtn(
              <>{regenerating ? <span className="ai-spin" style={{ width: 13, height: 13, border: "2px solid var(--alpha-teal-20)", borderTopColor: "var(--alpha-teal)", borderRadius: 9999 }} /> : <Ic paths={["M3 12a9 9 0 1 0 9-9", "M3 4v5h5"]} size={14} sw={1.8} />}{regenerating ? "Improving" : "Edit / Improve"}</>,
              async () => { if (regenerating) return; setRegenerating(true); try { await onRegenerate(artifact.id); } finally { setRegenerating(false); } },
              regenerating
            )}
            {ghostBtn(
              <><Ic paths={["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"]} size={14} sw={1.8} />Download</>,
              () => { try { const b = new Blob([text], { type: "text/plain" }); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = (artifact.name || "asset").replace(/[^a-z0-9]+/gi, "-").toLowerCase() + ".txt"; a.click(); URL.revokeObjectURL(u); } catch { /* ignore */ } }
            )}
          </div>

          <button onClick={onClose} style={{ position: "absolute", top: 20, right: 22, background: "none", border: "none", cursor: "pointer", color: "var(--muted-foreground)", padding: 2 }}>
            <Ic paths={["M18 6 6 18M6 6l12 12"]} size={20} sw={2} />
          </button>
        </div>

        {/* body */}
        <div className="ai-scroll" style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
          <ClaimCheckDetails check={artifact.claimCheck} />
          {artifact.intent && (
            <>
              <h4 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Strategic intent</h4>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--foreground)", margin: "0 0 16px" }}>{artifact.intent}</p>
            </>
          )}
          {artifact.aiReasoningBehavior && (
            <div style={{ background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--foreground)", marginBottom: 5 }}>AI reasoning behavior influenced</div>
              <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>{artifact.aiReasoningBehavior}</p>
            </div>
          )}
          <h4 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px" }}>Full content</h4>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "18px 20px" }}>
            <FullContentRenderer content={artifact.fullContent || {}} />
          </div>
        </div>
      </div>
      <ExplainPopover anchor={explain} onClose={() => setExplain(null)} />
    </div>
  );
}
